import { createFeature, createReducer, on } from '@ngrx/store';
import { Tag, TagType } from '../entities/tags.entity';
import { tagsActions, tagsApiActions } from './tags.actions';

export interface TagsState {
  tagType: TagType;
  activatedTagIds: string[];
  tags: Record<TagType, Tag[]>;
  currentlyEditingEntityId: string;
  addTagError: string;
}

const initialState: TagsState = {
  tagType: null,
  activatedTagIds: null,
  tags: null,
  currentlyEditingEntityId: null,
  addTagError: null,
};

export const tagsFeature = createFeature({
  name: 'tags',
  reducer: createReducer(
    initialState,
    on(
      tagsActions.loadTags,
      (state, { tagType }): TagsState => ({
        ...state,
        tagType,
      }),
    ),
    on(
      tagsApiActions.loadPositionTagsSuccess,
      (state, { tags }): TagsState => ({
        ...state,
        tags: {
          ...state.tags,
          position: tags,
        },
      }),
    ),
    on(
      tagsApiActions.loadWatchlistTagsSuccess,
      (state, { tags }): TagsState => ({
        ...state,
        tags: {
          ...state.tags,
          watchlist: tags,
        },
      }),
    ),
    on(tagsActions.presetActivatedTags, (state, { tagIds }): TagsState => ({ ...state, activatedTagIds: tagIds })),
    on(tagsApiActions.deleteTagSuccess, (state, { tagId }): TagsState => {
      const tags = state.tags[state.tagType].filter((tag) => tag.tagId !== tagId);
      return {
        ...state,
        tags: {
          ...state.tags,
          [state.tagType]: tags,
        },
      };
    }),
    on(tagsActions.setCurrentlyEditingEntityId, (state, { entityId }): TagsState => ({ ...state, currentlyEditingEntityId: entityId })),
    on(tagsActions.resetCurrentlyEditingEntityId, (state): TagsState => ({ ...state, currentlyEditingEntityId: null })),
    on(tagsApiActions.deleteLinkedTagSuccess, (state, { tagId, entityId }): TagsState => {
      const tag = state.tags[state.tagType]?.find((tag) => tag.tagId === tagId);
      if (!tag) {
        return state;
      }

      const updatedEntities = tag.entities.filter((entity) => entity.entityId !== entityId);
      return {
        ...state,
        tags: {
          ...state.tags,
          [state.tagType]: state.tags[state.tagType].map((t) => (t.tagId === tagId ? { ...t, entities: updatedEntities } : t)),
        },
      };
    }),
    on(tagsApiActions.addTagSuccess, (state, { tag }): TagsState => {
      const { tags, tagType } = state;
      const tagList = tags[tagType] || [];

      const updatedTags = tagList.some((t) => t.tagId === tag.tagId)
        ? tagList.map((t) => (t.tagId === tag.tagId ? tag : t))
        : [...tagList, tag];

      updatedTags.sort((a, b) => a.name.localeCompare(b.name));

      return {
        ...state,
        tags: {
          ...tags,
          [tagType]: updatedTags,
        },
        addTagError: null,
      };
    }),
    on(tagsApiActions.addTagFailure, (state, { error }): TagsState => ({ ...state, addTagError: error })),
    on(tagsActions.clearErrors, (state): TagsState => ({ ...state, addTagError: null })),
  ),
});
